import { useQuery } from '@apollo/client'
import { Box, ChartColumnBig, Lock, Settings, Users } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { getProjectUsersAccess } from '@/lib/rbac'
import { cn } from '@/lib/utils'
import { PROJECT_DISPLAY_NAME_QUERY } from '@/routes/project/queries'
import type {
  ProjectDisplayNameQuery,
  ProjectDisplayNameQueryVariables,
} from '@/routes/project/queries.types.gen'

const PROJECT_NAV_ITEMS = [
  {
    access: 'project',
    title: 'Services',
    url: '/project/:projectName/services',
    icon: Box,
    activeTags: ['services'],
  },
  {
    access: 'usage',
    title: 'Usage',
    url: '/project/:projectName/usage',
    icon: ChartColumnBig,
    activeTags: ['/usage'],
  },
  {
    access: 'users',
    title: 'Access management',
    url: '/project/:projectName/access-management',
    icon: Users,
    activeTags: ['/access-management'],
  },
  {
    access: 'project',
    title: 'Settings',
    url: '/project/:projectName/settings',
    icon: Settings,
    activeTags: ['/settings'],
  },
] as const

export const ProjectNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { projectName = '' } = useParams<{ projectName: string }>()
  const { data } = useQuery<
    ProjectDisplayNameQuery,
    ProjectDisplayNameQueryVariables
  >(PROJECT_DISPLAY_NAME_QUERY, {
    variables: { idCompany, projectName },
    fetchPolicy: 'cache-only',
  })

  const idProject = useMemo(
    () => data?.company?.projectByName?.id ?? '',
    [data]
  )
  const projectRbac = useMemo(
    () => rbac.company(idCompany).project(idProject),
    [rbac, idCompany, idProject]
  )
  const projectUsersAccess = useMemo(
    () => getProjectUsersAccess(projectRbac),
    [projectRbac]
  )
  const items = useMemo(
    () =>
      PROJECT_NAV_ITEMS.map((item) => ({
        ...item,
        hasAccess:
          data === undefined
            ? true
            : item.access === 'usage'
              ? projectRbac.usage.view.can()
              : item.access === 'users'
                ? projectUsersAccess.canView
                : projectRbac.view.can(),
      })),
    [data, projectRbac, projectUsersAccess.canView]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !item.hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!item.hasAccess}
                >
                  {item.hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { projectName },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
